import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { connect } from "react-redux";
import {
  getFindPetInfo,
  sendLocation,
  claimQRCode,
  updateClaimedQR,
  clearClaimState,
  ownerLogin,
  ownerUpdateQR,
  ownerToggleLost,
  ownerLogout,
  sendOTP,
  verifyOTP,
  clearOTPState,
} from "../actions/orderActions.js";
import { addNewChatUser, clearChatUsers } from "../actions/chatActions";
import useScrollLock from "../hooks/useScrollLock";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import Logo from "../components/Logo";
import {
  Dog,
  PawPrint,
  VenusAndMars,
  Palette,
  User,
  MapPin,
  Phone,
  Mail,
  Building2,
  LocateFixed,
  Navigation,
  Lock,
  Smartphone,
  ImagePlus,
  MessageSquareMore,
  Syringe,
  MapPinned,
  Navigation2,
} from "lucide-react";

const lngs = ["ka", "en"]; //  "ru"
const lngsName = { ka: "ქართული", en: "English", ru: "Русский" };
const lngsNameForFindPetPage = { ka: "ქართ", en: "ENG", ru: "Русский" };

// QR Collar color to image mapping
const COLLAR_COLOR_IMAGES = {
  orange: "/img/claim-tag-orange-1.webp",
  cream: "/img/claim-tag-cream-1.webp",
  black: "/img/claim-tag-black.webp",
  pink: "/img/claim-tag-pink.webp",
};

// QR Collar color to image mapping
const ACTIVATED_COLLAR_COLOR_IMAGES = {
  orange: "/img/OrangeTagActivated.webp",
  cream: "/img/CreamTagActivated.webp",
  black: "/img/BlackTagActivated.webp",
  pink: "/img/PinkTagActivated.webp",
};

// Map backend error messages to translation keys
const ERROR_TRANSLATION_MAP = {
  "Phone number does not match the registered owner": "error_phone_not_match",
  "No owner phone number registered for this QR code": "error_no_owner_phone",
  "QR code not found": "error_qr_not_found",
  "Please wait before requesting a new code": "error_wait_before_resend",
  "Invalid phone number format. Use 9 digits.": "error_invalid_phone_format",
  "Phone number is required": "error_phone_required",
  "Failed to send SMS. Please try again.": "error_sms_failed",
  "Verification code has expired. Please request a new one.":
    "error_code_expired",
  "Too many failed attempts. Please request a new code.":
    "error_too_many_attempts",
  "Invalid verification code": "error_invalid_code",
  "No verification code found. Please request a new one.":
    "error_no_code_found",
};

// Helper function to get translated error message
const getTranslatedError = (error, t) => {
  if (!error) return null;
  const translationKey = ERROR_TRANSLATION_MAP[error];
  return translationKey ? t(translationKey) : error;
};

// LocalStorage key for storing QR IDs that the user has logged into as owner
const OWNER_QR_IDS_KEY = "pethub_owner_qr_ids";

// Helper to check if this device was previously logged in as owner for this QR
const isDeviceOwnerOfQR = (qrId) => {
  try {
    const ownerQRIds = JSON.parse(
      localStorage.getItem(OWNER_QR_IDS_KEY) || "[]",
    );
    return ownerQRIds.includes(qrId);
  } catch {
    return false;
  }
};

// Helper to save QR ID as owner-accessed on this device
const saveOwnerQRId = (qrId) => {
  try {
    const ownerQRIds = JSON.parse(
      localStorage.getItem(OWNER_QR_IDS_KEY) || "[]",
    );
    if (!ownerQRIds.includes(qrId)) {
      ownerQRIds.push(qrId);
      localStorage.setItem(OWNER_QR_IDS_KEY, JSON.stringify(ownerQRIds));
    }
  } catch {
    // Ignore localStorage errors
  }
};

// სიტყვის პირველ ასოს ადიდებს ხოლო დანარჩენს აპატარავებს:
const formatName = (name) => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

const FindPet = ({
  getFindPetInfo,
  sendLocation,
  claimQRCode,
  updateClaimedQR,
  clearClaimState,
  ownerLogin,
  ownerUpdateQR,
  ownerToggleLost,
  ownerLogout,
  sendOTP,
  verifyOTP,
  clearOTPState,
  findPetInfo,
  claimLoading,
  claimSuccess,
  claimError,
  ownerLoginLoading,
  ownerLoggedIn,
  ownerLoginError,
  otpSending,
  otpSent,
  otpSendError,
  otpPhoneNumber,
  otpVerifying,
  otpVerifyError,
  otpSecondsRemaining,
  addNewChatUser,
  clearChatUsers,
  darkMode,
}) => {
  const { t, i18n } = useTranslation();
  const { qrID } = useParams();
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationDenied, setShowLocationDenied] = useState(false);

  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const hasSeenWelcomeModal = localStorage.getItem(`welcome_seen_${qrID}`);

  const isModalOpen = showWelcomeModal && (isEditMode || ownerLoggedIn);
  useScrollLock(isModalOpen);

  // Owner login state
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginPhoneNumber, setLoginPhoneNumber] = useState("");
  const [editFormData, setEditFormData] = useState({});
  const [editImagePreview, setEditImagePreview] = useState(null);

  // OTP verification state
  const [otpCode, setOtpCode] = useState("");
  const [countdown, setCountdown] = useState(0);

  // Countdown timer for welcome modal
  useEffect(() => {
    let timer;
    if (showWelcomeModal && (isEditMode || ownerLoggedIn)) {
      timer = setTimeout(() => {
        setShowWelcomeModal(false);
      }, 40000);
    }
    return () => clearTimeout(timer);
  }, [showWelcomeModal, isEditMode, ownerLoggedIn]);

  // Countdown timer effect for OTP resend (also handles rate limit errors)
  useEffect(() => {
    if (otpSecondsRemaining > 0) {
      setCountdown(otpSecondsRemaining);
    }
  }, [otpSecondsRemaining]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  // Clear OTP state when login form is closed
  useEffect(() => {
    if (!showLoginForm) {
      clearOTPState();
      setOtpCode("");
      setCountdown(0);
    }
  }, [showLoginForm, clearOTPState]);

  // Initialize editFormData when owner is logged in and findPetInfo is available
  // Only initialize if form is empty to avoid overwriting user edits
  useEffect(() => {
    if ((ownerLoggedIn || isEditMode) && findPetInfo) {
      // Check if form is empty (not filled by user)
      const isFormEmpty =
        !editFormData.pet_name &&
        !editFormData.full_name &&
        !editFormData.phone_number;
      if (isFormEmpty) {
        setEditFormData({
          pet_name: findPetInfo.pet_name || "",
          pet_type: findPetInfo.pet_type || "dog",
          pet_breed: findPetInfo.pet_breed || "",
          gender: findPetInfo.gender || "male",
          pet_color: findPetInfo.pet_color || "",
          full_name: findPetInfo.full_name || "",
          phone_number: findPetInfo.phone_number || "",
          second_phone_number: findPetInfo.second_phone_number || "",
          city: findPetInfo.city || "",
          address: findPetInfo.address || "",
          comment: findPetInfo.comment || "",
          is_vaccinated: findPetInfo.is_vaccinated || false,
        });
        if (findPetInfo.pet_image) {
          setEditImagePreview(findPetInfo.pet_image);
        }
      }
    }
  }, [ownerLoggedIn, isEditMode, findPetInfo]);

  // Claim flow state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showPetForm, setShowPetForm] = useState(false);
  const [petImagePreview, setPetImagePreview] = useState(null);
  const [petFormData, setPetFormData] = useState({
    pet_name: "",
    pet_type: "dog",
    pet_breed: "",
    gender: "male",
    pet_color: "",
    full_name: "",
    phone_number: "",
    second_phone_number: "",
    city: "",
    address: "",
    comment: "",
    pet_image: null,
  });

  useEffect(() => {
    getFindPetInfo(qrID);
  }, []);

  // Debug: Log when findPetInfo changes
  useEffect(() => {
    console.log("findPetInfo changed:", {
      marked_as_lost: findPetInfo?.marked_as_lost,
      pet_name: findPetInfo?.pet_name,
      status: findPetInfo?.status,
    });
  }, [findPetInfo]);

  // Save QR ID to localStorage when owner logs in successfully
  // This prevents location sharing for this QR even after logout
  useEffect(() => {
    if (ownerLoggedIn && qrID) {
      saveOwnerQRId(qrID);
    }
  }, [ownerLoggedIn, qrID]);

  useEffect(() => {
    // Only send location for strangers/finders - NOT for owners
    // Location sharing should only trigger when:
    // 1. Pet is marked as lost
    // 2. User is NOT currently logged in as owner
    // 3. User has NEVER logged in as owner for this QR on this device
    const wasOwnerOnThisDevice = isDeviceOwnerOfQR(qrID);
    if (
      findPetInfo &&
      findPetInfo.marked_as_lost === true &&
      !ownerLoggedIn &&
      !wasOwnerOnThisDevice
    ) {
      if (navigator.permissions && navigator.geolocation) {
        navigator.permissions.query({ name: "geolocation" }).then((result) => {
          if (result.state === "granted" || result.state === "prompt") {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const { latitude, longitude } = position.coords;
                sendLocation(qrID, { latitude, longitude });
                setUserLocation({ latitude, longitude });
              },
              (error) => {
                console.warn("Geolocation error:", error.message);
              },
            );
          } else {
            console.log("Geolocation access denied.");
          }
        });
      }
    }
  }, [findPetInfo, ownerLoggedIn, qrID]);

  const handleShareLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        sendLocation(qrID, { latitude, longitude });
        setUserLocation({ latitude, longitude });
        setShowLocationDenied(false);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setShowLocationDenied(true);
        } else {
          alert("Error getting location: " + error.message);
        }
      },
    );
  };

  // Handle claim QR code - Step 1: Send OTP
  const handleClaimSendOTP = async () => {
    // Normalize phone number
    let phone = phoneNumber.replace(/\s+/g, "").replace(/-/g, "");
    if (phone.startsWith("+995")) phone = phone.slice(4);
    else if (phone.startsWith("995")) phone = phone.slice(3);
    else if (phone.startsWith("0")) phone = phone.slice(1);

    if (!phone || phone.length !== 9 || !/^\d+$/.test(phone)) {
      alert(t("please_enter_valid_phone"));
      return;
    }
    try {
      await sendOTP(qrID, phone);
      setCountdown(60); // 1 minute
    } catch (error) {
      console.error("Send OTP error:", error);
    }
  };

  // Handle claim QR code - Step 2: Verify OTP and claim
  const handleVerifyClaimOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      alert(t("otp_invalid_code"));
      return;
    }
    // Normalize phone number
    let phone = phoneNumber.replace(/\s+/g, "").replace(/-/g, "");
    if (phone.startsWith("+995")) phone = phone.slice(4);
    else if (phone.startsWith("995")) phone = phone.slice(3);
    else if (phone.startsWith("0")) phone = phone.slice(1);

    try {
      // First verify OTP
      await verifyOTP(qrID, phone, otpCode);
      // Then claim the QR code
      await claimQRCode(qrID, phone);

      if (!hasSeenWelcomeModal) {
        setShowWelcomeModal(true);
        localStorage.setItem(`welcome_seen_${qrID}`, "true");
      }
      // Save QR ID as owner - prevents location sharing on this device
      saveOwnerQRId(qrID);
      // Pre-fill phone number in edit form
      setEditFormData((prev) => ({ ...prev, phone_number: phone }));
      // Transition to the owner edit view (same component used for subsequent logins)
      setIsEditMode(true);
      // Clear claim state so the claimable block (line 625) doesn't catch us after save
      clearClaimState();
      // Clear OTP state
      clearOTPState();
      setOtpCode("");
    } catch (error) {
      console.error("Verify/Claim error:", error);
    }
  };

  // Handle resend OTP for claim flow
  const handleClaimResendOTP = async () => {
    if (countdown > 0) return;
    setOtpCode("");
    await handleClaimSendOTP();
  };

  // Handle pet form input changes
  const handlePetFormChange = (e) => {
    const { name, value } = e.target;
    setPetFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle pet form submission
  const handleSavePetInfo = async () => {
    if (!editFormData.pet_name) {
      alert(t("please_enter_pet_name"));
      return;
    }
    try {
      await updateClaimedQR(qrID, editFormData);
      // Refresh the page data
      getFindPetInfo(qrID);
    } catch (error) {
      console.error("Update error:", error);
    }
  };

  // Handle logout - clear tokens and redirect to default view
  const handleLogout = () => {
    ownerLogout();
    clearClaimState();
    setIsEditMode(false);
    setShowPetForm(false);
    setShowLoginForm(false);
    setLoginPhoneNumber("");
    setPhoneNumber("");
    setPetImagePreview(null);
    setEditFormData({});
    setEditImagePreview(null);
    setPetFormData({
      pet_name: "",
      pet_type: "dog",
      pet_breed: "",
      gender: "male",
      pet_color: "",
      full_name: "",
      phone_number: "",
      second_phone_number: "",
      city: "",
      address: "",
      comment: "",
      pet_image: null,
    });
    // Use setTimeout to ensure Redux state is processed
    // before refreshing data, preventing the form from briefly re-showing
    setTimeout(() => {
      getFindPetInfo(qrID);
    }, 0);
  };

  // Handle pet image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPetFormData((prev) => ({ ...prev, pet_image: [file] }));
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPetImagePreview(previewUrl);
    }
  };

  // Handle send OTP
  const handleSendOTP = async () => {
    // Normalize phone number - remove spaces and common prefixes
    let phone = loginPhoneNumber.replace(/\s+/g, "").replace(/-/g, "");
    if (phone.startsWith("+995")) phone = phone.slice(4);
    else if (phone.startsWith("995")) phone = phone.slice(3);
    else if (phone.startsWith("0")) phone = phone.slice(1);

    if (!phone || phone.length !== 9 || !/^\d+$/.test(phone)) {
      alert(t("please_enter_valid_phone"));
      return;
    }
    try {
      await sendOTP(qrID, phone);
      setCountdown(60); // 1 minute
    } catch (error) {
      console.error("Send OTP error:", error);
    }
  };

  // Handle verify OTP
  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      alert(t("otp_invalid_code"));
      return;
    }
    // Normalize phone number
    let phone = loginPhoneNumber.replace(/\s+/g, "").replace(/-/g, "");
    if (phone.startsWith("+995")) phone = phone.slice(4);
    else if (phone.startsWith("995")) phone = phone.slice(3);
    else if (phone.startsWith("0")) phone = phone.slice(1);

    try {
      await verifyOTP(qrID, phone, otpCode);
      setShowLoginForm(false);
      setIsEditMode(true);
      // Pre-fill edit form with current data
      setEditFormData({
        pet_name: findPetInfo.pet_name || "",
        pet_type: findPetInfo.pet_type || "dog",
        pet_breed: findPetInfo.pet_breed || "",
        gender: findPetInfo.gender || "male",
        pet_color: findPetInfo.pet_color || "",
        full_name: findPetInfo.full_name || "",
        phone_number: findPetInfo.phone_number || "",
        second_phone_number: findPetInfo.second_phone_number || "",
        city: findPetInfo.city || "",
        address: findPetInfo.address || "",
        comment: findPetInfo.comment || "",
        is_vaccinated: findPetInfo.is_vaccinated || false,
      });
      if (findPetInfo.pet_image) {
        setEditImagePreview(findPetInfo.pet_image);
      }
      clearOTPState();
      setOtpCode("");
    } catch (error) {
      console.error("Verify OTP error:", error);
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    if (countdown > 0) return;
    setOtpCode("");
    await handleSendOTP();
  };

  // Legacy owner login (kept for backward compatibility but not used)
  const handleOwnerLogin = async () => {
    handleSendOTP();
  };

  // Handle edit form changes
  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle edit image change
  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditFormData((prev) => ({ ...prev, pet_image: [file] }));
      const previewUrl = URL.createObjectURL(file);
      setEditImagePreview(previewUrl);
    }
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      await ownerUpdateQR(qrID, editFormData);
      // Refresh the page data
      getFindPetInfo(qrID);
      setIsEditMode(false);
    } catch (error) {
      console.error("Update error:", error);
    }
  };

  // Handle toggle lost status
  const handleToggleLost = async () => {
    try {
      console.log(
        "Toggle clicked, current state:",
        findPetInfo?.marked_as_lost,
      );
      const result = await ownerToggleLost(qrID);
      console.log("Toggle API response:", result);
      // Refresh the page data
      await getFindPetInfo(qrID);
      console.log("After refresh, state:", findPetInfo?.marked_as_lost);
    } catch (error) {
      console.error("Toggle lost error:", error);
    }
  };

  // Handle owner logout
  const handleOwnerLogout = () => {
    ownerLogout();
    clearClaimState();
    setIsEditMode(false);
    setShowPetForm(false);
    setShowLoginForm(false);
    setLoginPhoneNumber("");
    setPhoneNumber("");
    setEditFormData({});
    setEditImagePreview(null);
    setPetImagePreview(null);
    // Refresh page data after state is cleared
    setTimeout(() => {
      getFindPetInfo(qrID);
    }, 0);
  };

  const renderSecondNumber = () => {};

  const iconStyle = {
    color: "#595959",
    strokeWidth: 1.5,
    size: 20,
  };

  const iconStyle2 = {
    color: "#ff6500",
    strokeWidth: 1.5,
    size: 20,
  };

  const uploadPetPhoto = {
    color: "#595959",
    strokeWidth: 1.1,
    size: 40,
  };

  if (findPetInfo === null) {
    return null;
  }

  // Hide page for "available" status QR codes (not yet purchased/claimed)
  if (findPetInfo.status === "available") {
    return (
      <>
        <div className="binder">
          <p>Please use a mobile phone or tablet to use the page</p>
        </div>
        <div className="find-pet-info-container claim-container">
          <div className="find-pet-info-container-header">
            <div className="pet-photo-and-name-container">
              <Logo darkMode={darkMode} />
            </div>
          </div>
          <div className="find-pet-info-container-body">
            <div className="logo-button-container">
              <div className="locked-info">
                <img
                  className="locked-info-svg"
                  src="/svg/lock.svg"
                  alt="Locked"
                />
                <p className="locked-info-text">{t("information hidden")}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Handle claimable QR codes (reserved for retail store purchases)
  // Skip this block if isEditMode is true (user just claimed and transitioned to owner view)
  if (
    (findPetInfo.status === "claimable" || showPetForm || claimSuccess) &&
    !isEditMode
  ) {
    // Show pet info form after successful claim
    if (showPetForm || claimSuccess) {
      return (
        <>
          <div className="binder">
            <p>Please use a mobile phone or tablet to use the page</p>
          </div>
          <div className="find-pet-info-container claim-container">
            <div className="find-pet-info-container-header">
              {/* Pet Image Upload Section */}
              {/* <div className="form-section-title">
                {t("setup_your_pet_desc")}
              </div> */}
              <div className="dropdown-menu-for-findpet-page">
                {/* Language Switcher */}
                <div className="find-pet-info-language-switcher">
                  {lngs.map((lng) => {
                    if (lng === localStorage.getItem("i18nextLng")) {
                      return null;
                    } else {
                      return (
                        <span
                          key={lng}
                          onClick={() => i18n.changeLanguage(lng)}
                          disabled={i18n.resolvedLanguage === lng}
                        >
                          {lngsNameForFindPetPage[lng]}
                        </span>
                      );
                    }
                  })}
                </div>
              </div>

              <div className="form-group image-upload-group">
                <div className="image-upload-container">
                  {petImagePreview ||
                  editImagePreview ||
                  findPetInfo?.pet_image ? (
                    <div className="image-preview-wrapper">
                      <img
                        src={
                          petImagePreview ||
                          editImagePreview ||
                          findPetInfo?.pet_image
                        }
                        alt="Pet preview"
                        className="pet-image-preview"
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => {
                          setPetImagePreview(null);
                          setEditImagePreview(null);
                          setPetFormData((prev) => ({
                            ...prev,
                            pet_image: null,
                          }));
                          setEditFormData((prev) => ({
                            ...prev,
                            pet_image: null,
                          }));
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="image-upload-label">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="image-upload-input"
                      />
                      <div className="image-upload-placeholder">
                        <ImagePlus {...uploadPetPhoto} />
                        <span>{t("add_pet_photo")}</span>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="find-pet-info-container-body">
              <div className="logo-button-container">
                <div className="logo-button">
                  <Logo darkMode={darkMode} />
                </div>
                {/* <h4 className="claim-title" style={{ color: "#000" }}>
                  {t("setup_your_pet")}
                </h4> */}
                {/* <p className="claim-subtitle" style={{ color: "#000" }}>
                  {t("setup_your_pet_desc")}
                </p> */}
              </div>

              <div className="claim-form pet-info-form">
                {/* Pet Status Section */}
                <div className="form-section-title">{t("current_status")}</div>
                <div className="pet-status-section">
                  <button
                    className={`status-toggle-btn switch ${
                      findPetInfo.marked_as_lost
                        ? "btn-mark-safe"
                        : "btn-mark-lost"
                    }`}
                    onClick={handleToggleLost}
                    role="switch"
                    aria-checked={findPetInfo.marked_as_lost}
                  >
                    <span className="switch-label">
                      {t("profile_open_lock")}
                    </span>

                    <span className="switch-track">
                      <span className="switch-thumb" />
                    </span>
                  </button>

                  <div
                    className={`pet-status-card ${
                      findPetInfo.marked_as_lost ? "status-lost" : "status-safe"
                    }`}
                  >
                    <div className="status-content">
                      <span className="status-description">
                        {findPetInfo.marked_as_lost
                          ? t("lost_status_description")
                          : t("safe_status_description")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Owner Information Section */}
                <div className="form-section-title">{t("owner_info")}</div>
                <div className="form-row">
                  <div className="form-group">
                    <User {...iconStyle} />
                    <input
                      type="text"
                      name="full_name"
                      value={editFormData.full_name || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_name")}
                    />
                  </div>

                  <div className="form-group">
                    <Phone {...iconStyle} />
                    <input
                      type="tel"
                      name="phone_number"
                      value={editFormData.phone_number || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_phone")}
                    />
                  </div>

                  <div className="form-group">
                    <Phone {...iconStyle} />
                    <input
                      type="tel"
                      name="second_phone_number"
                      value={editFormData.second_phone_number || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_second_phone")}
                    />
                  </div>

                  <div className="form-group">
                    <Building2 {...iconStyle} />
                    <input
                      type="text"
                      name="city"
                      value={editFormData.city || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_city")}
                    />
                  </div>

                  <div className="form-group">
                    <MapPin {...iconStyle} />
                    <input
                      type="text"
                      name="address"
                      value={editFormData.address || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_address")}
                    />
                  </div>
                </div>
                {/* Pet Information Section */}
                <div className="form-section-title">{t("pet_info")}</div>
                <div className="form-row">
                  <div className="form-group">
                    <Dog {...iconStyle} />
                    <input
                      type="text"
                      name="pet_name"
                      value={editFormData.pet_name || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_pet_name")}
                    />
                  </div>

                  <div className="form-group">
                    <Dog {...iconStyle} />
                    <select
                      name="pet_type"
                      value={editFormData.pet_type || "dog"}
                      onChange={handleEditFormChange}
                    >
                      <option value="dog">{t("dog")}</option>
                      <option value="cat">{t("cat")}</option>
                      <option value="bird">{t("bird")}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <PawPrint {...iconStyle} />
                    <input
                      type="text"
                      name="pet_breed"
                      value={editFormData.pet_breed || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_breed")}
                    />
                  </div>

                  <div className="form-group">
                    <VenusAndMars {...iconStyle} />
                    <select
                      name="gender"
                      value={editFormData.gender || "male"}
                      onChange={handleEditFormChange}
                    >
                      <option value="male">{t("male")}</option>
                      <option value="female">{t("female")}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <Palette {...iconStyle} />
                    <input
                      type="text"
                      name="pet_color"
                      value={editFormData.pet_color || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_color")}
                    />
                  </div>
                </div>
                <div className="form-group checkbox-group">
                  <Syringe {...iconStyle} />
                  <label>
                    <input
                      type="checkbox"
                      name="is_vaccinated"
                      checked={editFormData.is_vaccinated || false}
                      onChange={handleEditFormChange}
                    />
                    <span>{t("vaccinated")}</span>
                  </label>
                </div>
                <div className="form-group">
                  <MessageSquareMore {...iconStyle} />
                  <textarea
                    name="comment"
                    value={editFormData.comment || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_comment")}
                    rows="3"
                  />
                </div>
                <button
                  className="claim-submit-btn"
                  onClick={handleSavePetInfo}
                >
                  {t("save")}
                </button>
                <button className="claim-logout-btn" onClick={handleLogout}>
                  {t("logout")}
                </button>
              </div>
            </div>
          </div>
        </>
      );
    }

    // Show claim form (phone number input)
    return (
      <>
        <div className="binder">
          <p>Please use a mobile phone or tablet to use the page</p>
        </div>
        <div className="find-pet-info-container">
          <div className="find-pet-info-container-header">
            <div className="pet-photo-and-name-container">
              <div className="dropdown-menu-for-findpet-page right">
                {/* Language Switcher */}
                <div className="find-pet-info-language-switcher">
                  {lngs.map((lng) => {
                    if (lng === localStorage.getItem("i18nextLng")) {
                      return null;
                    } else {
                      return (
                        <span
                          key={lng}
                          onClick={() => i18n.changeLanguage(lng)}
                          disabled={i18n.resolvedLanguage === lng}
                        >
                          {lngsNameForFindPetPage[lng]}
                        </span>
                      );
                    }
                  })}
                </div>
              </div>

              {/* Hero image for unclaimed QR collar - shows collar color at top */}
              <div className="qr-collar-hero">
                <img
                  src={
                    COLLAR_COLOR_IMAGES[findPetInfo?.color] || "/img/1-1.webp"
                  }
                  alt="QR Collar Tag"
                  className="qr-collar-hero-img"
                />
              </div>
            </div>
          </div>

          <div className="find-pet-info-container-body">
            <div className="logo-button-container">
              <div className="logo-button">
                <Logo darkMode={darkMode} />
              </div>
              <h4 className="claim-title">{t("connect your tag")}</h4>
              {/* <p className="claim-subtitle" style={{ color: "#000" }}>
                {t("claim_your_collar_desc")}
              </p> */}
            </div>
            <div className="claim-form">
              {!otpSent ? (
                <>
                  {/* Step 1: Phone number input */}
                  <div className="form-group">
                    <label>{t("phone_number")}</label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder={t("enter_phone_placeholder")}
                      maxLength="15"
                    />
                  </div>

                  {otpSendError && (
                    <p
                      className="claim-error"
                      style={{
                        padding: "12px 16px",
                        fontSize: "0.9rem",
                        color: "#dc3545",
                        background: "#fff5f5",
                        border: "1px solid #ffebee",
                        borderRadius: "8px",
                        textAlign: "center",
                        marginBottom: "10px",
                      }}
                    >
                      {getTranslatedError(otpSendError.error, t) ||
                        t("otp_send_error")}
                      {countdown > 0 && (
                        <span
                          style={{
                            display: "block",
                            marginTop: "5px",
                            fontWeight: "600",
                            color: "#dc3545",
                          }}
                        >
                          {Math.floor(countdown / 60)}:
                          {(countdown % 60).toString().padStart(2, "0")}
                        </span>
                      )}
                    </p>
                  )}

                  <button
                    className="claim-submit-btn"
                    onClick={handleClaimSendOTP}
                    disabled={otpSending}
                  >
                    {otpSending ? t("loading") : t("lets go")}
                  </button>

                  <p className="claim-info-text">{t("claim_info_text")}</p>
                </>
              ) : (
                <>
                  {/* Step 2: OTP verification */}
                  <div className="form-group">
                    <label>{t("otp_enter_code")}</label>
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) =>
                        setOtpCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                      placeholder={t("otp_code_placeholder")}
                      maxLength="6"
                      style={{
                        textAlign: "center",
                        fontSize: "1rem",
                        letterSpacing: "0.5rem",
                      }}
                    />
                  </div>

                  <p
                    className="otp-sent-message"
                    style={{
                      color: "#666",
                      textAlign: "center",
                      marginBottom: "10px",
                    }}
                  >
                    {t("otp_sent")} ({phoneNumber})
                  </p>

                  {otpVerifyError && (
                    <p className="claim-error">
                      {getTranslatedError(otpVerifyError.error, t) ||
                        t("otp_invalid_code")}
                      {otpVerifyError.attempts_left !== undefined && (
                        <span>
                          {" "}
                          ({otpVerifyError.attempts_left}{" "}
                          {t("attempts_left") || "attempts left"})
                        </span>
                      )}
                    </p>
                  )}

                  {claimError && (
                    <p className="claim-error">
                      {claimError.error || t("claim_error")}
                    </p>
                  )}

                  <button
                    className="claim-submit-btn"
                    onClick={handleVerifyClaimOTP}
                    disabled={otpVerifying || claimLoading}
                  >
                    {otpVerifying || claimLoading
                      ? t("loading")
                      : t("otp_verify")}
                  </button>

                  <button
                    className="claim-resend-btn"
                    onClick={handleClaimResendOTP}
                    disabled={countdown > 0 || otpSending}
                    style={{
                      marginTop: "10px",
                      background: countdown > 0 ? "#ccc" : "#f5f5f5",
                      color: countdown > 0 ? "#999" : "#333",
                      border: "1px solid #ddd",
                      padding: "10px 20px",
                      borderRadius: "5px",
                      cursor: countdown > 0 ? "not-allowed" : "pointer",
                      width: "100%",
                    }}
                  >
                    {countdown > 0
                      ? `${t("otp_resend")} (${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, "0")})`
                      : t("otp_resend")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  if (findPetInfo !== null && !findPetInfo.marked_as_lost) {
    // Show edit mode if owner is logged in
    if (isEditMode || ownerLoggedIn) {
      return (
        <>
          <div className="binder">
            <p>Please use a mobile phone or tablet to use the page</p>
          </div>
          {/* მისალოცი მოდალი */}
          {showWelcomeModal && (
            <div className="welcome-modal-overlay">
              <div className="welcome-modal-content">
                {/* <Logo darkMode={darkMode} /> */}
                <img
                  className="activated-tag-img"
                  src={
                    ACTIVATED_COLLAR_COLOR_IMAGES[findPetInfo?.color] ||
                    "/img/OrangeTagActivated.webp"
                  }
                  alt="Activated Tag"
                  loading="lazy"
                />
                <h2>{t("congratulations")}!</h2>
                <h3>{t("tag is activated title")}</h3>
                <p>{t("tag is activated description")}</p>
                <button
                  className="modal-close-btn"
                  onClick={() => setShowWelcomeModal(false)}
                >
                  {t("ok")}
                </button>
              </div>
            </div>
          )}
          <div className="find-pet-info-container claim-container">
            <div className="find-pet-info-container-header">
              <div className="dropdown-menu-for-findpet-page">
                {/* Language Switcher */}
                <div className="find-pet-info-language-switcher">
                  {lngs.map((lng) => {
                    if (lng === localStorage.getItem("i18nextLng")) {
                      return null;
                    } else {
                      return (
                        <span
                          key={lng}
                          onClick={() => i18n.changeLanguage(lng)}
                          disabled={i18n.resolvedLanguage === lng}
                        >
                          {lngsNameForFindPetPage[lng]}
                        </span>
                      );
                    }
                  })}
                </div>
              </div>

              {/* Pet Image Upload Section */}
              {/* <div className="form-section-title">
                {t("update_your_pet_details")}
              </div> */}
              <div className="form-group image-upload-group">
                <div className="image-upload-container">
                  {editImagePreview ? (
                    <div className="image-preview-wrapper">
                      <img
                        src={editImagePreview}
                        alt="Pet preview"
                        className="pet-image-preview"
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => {
                          setEditImagePreview(null);
                          setEditFormData((prev) => ({
                            ...prev,
                            pet_image: null,
                          }));
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="image-upload-label">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleEditImageChange}
                        className="image-upload-input"
                      />
                      <div className="image-upload-placeholder">
                        <ImagePlus {...uploadPetPhoto} />
                        <span>{t("add_pet_photo")}</span>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="find-pet-info-container-body">
              <div className="logo-button-container">
                <div className="logo-button">
                  <Logo darkMode={darkMode} />
                </div>
              </div>

              <div className="claim-form pet-info-form">
                {/* Pet Status Section */}
                <div className="form-section-title">{t("current_status")}</div>
                <div className="pet-status-section">
                  <button
                    className={`status-toggle-btn switch ${
                      findPetInfo.marked_as_lost
                        ? "btn-mark-safe"
                        : "btn-mark-lost"
                    }`}
                    onClick={handleToggleLost}
                    role="switch"
                    aria-checked={findPetInfo.marked_as_lost}
                  >
                    <span className="switch-label">
                      {t("profile_open_lock")}
                    </span>

                    <span className="switch-track">
                      <span className="switch-thumb" />
                    </span>
                  </button>

                  <div
                    className={`pet-status-card ${
                      findPetInfo.marked_as_lost ? "status-lost" : "status-safe"
                    }`}
                  >
                    <div className="status-content">
                      <span className="status-description">
                        {findPetInfo.marked_as_lost
                          ? t("lost_status_description")
                          : t("safe_status_description")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Owner Information Section */}
                <div className="form-section-title">{t("owner_info")}</div>

                <div className="form-row">
                  <div className="form-group">
                    <User {...iconStyle} />
                    <input
                      type="text"
                      name="full_name"
                      value={editFormData.full_name || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_name")}
                    />
                  </div>

                  <div className="form-group">
                    <Phone {...iconStyle} />
                    <input
                      type="tel"
                      name="phone_number"
                      value={editFormData.phone_number || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_phone")}
                    />
                  </div>

                  <div className="form-group">
                    <Phone {...iconStyle} />
                    <input
                      type="tel"
                      name="second_phone_number"
                      value={editFormData.second_phone_number || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_second_phone")}
                    />
                  </div>

                  <div className="form-group">
                    <Building2 {...iconStyle} />
                    <input
                      type="text"
                      name="city"
                      value={editFormData.city || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_city")}
                    />
                  </div>

                  <div className="form-group">
                    <MapPin {...iconStyle} />
                    <input
                      type="text"
                      name="address"
                      value={editFormData.address || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_address")}
                    />
                  </div>
                </div>

                {/* Pet Information Section */}
                <div className="form-section-title">{t("pet_info")}</div>

                <div className="form-row">
                  <div className="form-group">
                    <Dog {...iconStyle} />
                    <input
                      type="text"
                      name="pet_name"
                      value={editFormData.pet_name || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_pet_name")}
                    />
                  </div>

                  <div className="form-group">
                    <Dog {...iconStyle} />
                    <select
                      name="pet_type"
                      value={editFormData.pet_type || "dog"}
                      onChange={handleEditFormChange}
                    >
                      <option value="dog">{t("dog")}</option>
                      <option value="cat">{t("cat")}</option>
                      <option value="bird">{t("bird")}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <PawPrint {...iconStyle} />
                    <input
                      type="text"
                      name="pet_breed"
                      value={editFormData.pet_breed || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_breed")}
                    />
                  </div>

                  <div className="form-group">
                    <VenusAndMars {...iconStyle} />
                    <select
                      name="gender"
                      value={editFormData.gender || "male"}
                      onChange={handleEditFormChange}
                    >
                      <option value="male">{t("male")}</option>
                      <option value="female">{t("female")}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <Palette {...iconStyle} />
                    <input
                      type="text"
                      name="pet_color"
                      value={editFormData.pet_color || ""}
                      onChange={handleEditFormChange}
                      placeholder={t("enter_color")}
                    />
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <Syringe {...iconStyle} />
                  <label>
                    <input
                      type="checkbox"
                      name="is_vaccinated"
                      checked={editFormData.is_vaccinated || false}
                      onChange={handleEditFormChange}
                    />
                    <span>{t("vaccinated")}</span>
                  </label>
                </div>

                <div className="form-group">
                  <MessageSquareMore {...iconStyle} />
                  <textarea
                    name="comment"
                    value={editFormData.comment || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_comment")}
                    rows="3"
                  />
                </div>

                <button className="claim-submit-btn" onClick={handleSaveEdit}>
                  {t("save")}
                </button>

                <button
                  className="claim-logout-btn"
                  onClick={handleOwnerLogout}
                >
                  {t("logout")}
                </button>
              </div>
            </div>
          </div>
        </>
      );
    }

    // Show login form
    if (showLoginForm) {
      return (
        <>
          <div className="binder">
            <p>Please use a mobile phone or tablet to use the page</p>
          </div>
          <div className="find-pet-info-container">
            <div className="find-pet-info-container-header">
              <div className="pet-photo-and-name-container">
                <div className="dropdown-menu-for-findpet-page right">
                  {/* Language Switcher */}
                  <div className="find-pet-info-language-switcher">
                    {lngs.map((lng) => {
                      if (lng === localStorage.getItem("i18nextLng")) {
                        return null;
                      } else {
                        return (
                          <span
                            key={lng}
                            onClick={() => i18n.changeLanguage(lng)}
                            disabled={i18n.resolvedLanguage === lng}
                          >
                            {lngsNameForFindPetPage[lng]}
                          </span>
                        );
                      }
                    })}
                  </div>
                </div>

                {/* Pet's picture above Logo */}
                <img
                  src={findPetInfo.pet_image || "/img/pet-placeholder.webp"}
                  alt="Pet photo"
                />

                <div className="pet-name-and-mark-as-lost-button-container">
                  <p className="pet-name">
                    {formatName(findPetInfo.pet_name) || t("unnamed_pet")}
                  </p>
                  <div className="vaccinated-container">
                    {findPetInfo.is_vaccinated ? (
                      <p className="vaccinated">#{t("vaccinated")}</p>
                    ) : (
                      <p className="not-vaccinated">#{t("not vaccinated")}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="find-pet-info-container-body">
              <div className="logo-button-container">
                <div className="logo-button">
                  <Logo darkMode={darkMode} />
                </div>
                <h4 className="claim-title">{t("owner_login")}</h4>
                {/* <p className="claim-subtitle" style={{ color: "#000" }}>
                  {t("enter_phone_to_edit")}
                </p> */}
              </div>
              <div className="claim-form">
                {!otpSent ? (
                  <>
                    {/* Step 1: Phone number input */}
                    <div className="form-group">
                      <label>{t("phone_number")}</label>
                      <input
                        type="tel"
                        value={loginPhoneNumber}
                        onChange={(e) => setLoginPhoneNumber(e.target.value)}
                        placeholder={t("enter_phone_placeholder")}
                        maxLength="15"
                      />
                    </div>

                    {otpSendError && (
                      <p
                        className="claim-error"
                        style={{
                          padding: "12px 16px",
                          fontSize: "0.9rem",
                          color: "#dc3545",
                          background: "#fff5f5",
                          border: "1px solid #ffebee",
                          borderRadius: "8px",
                          textAlign: "center",
                          marginBottom: "10px",
                        }}
                      >
                        {getTranslatedError(otpSendError.error, t) ||
                          t("otp_send_error")}
                        {countdown > 0 && (
                          <span
                            style={{
                              display: "block",
                              marginTop: "5px",
                              fontWeight: "600",
                              color: "#dc3545",
                            }}
                          >
                            {Math.floor(countdown / 60)}:
                            {(countdown % 60).toString().padStart(2, "0")}
                          </span>
                        )}
                      </p>
                    )}

                    <button
                      className="claim-submit-btn"
                      onClick={handleSendOTP}
                      disabled={otpSending}
                    >
                      {otpSending ? t("loading") : t("send_code")}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Step 2: OTP verification */}
                    <div className="form-group">
                      <label>{t("otp_enter_code")}</label>
                      <input
                        type="text"
                        value={otpCode}
                        onChange={(e) =>
                          setOtpCode(
                            e.target.value.replace(/\D/g, "").slice(0, 6),
                          )
                        }
                        placeholder={t("otp_code_placeholder")}
                        maxLength="6"
                        style={{
                          textAlign: "center",
                          fontSize: "1rem",
                          letterSpacing: "0.5rem",
                        }}
                      />
                    </div>

                    <p
                      className="otp-sent-message"
                      style={{
                        color: "#666",
                        textAlign: "center",
                        marginBottom: "10px",
                      }}
                    >
                      {t("otp_sent")} ({loginPhoneNumber})
                    </p>

                    {otpVerifyError && (
                      <p className="claim-error">
                        {getTranslatedError(otpVerifyError.error, t) ||
                          t("otp_invalid_code")}
                        {otpVerifyError.attempts_left !== undefined && (
                          <span>
                            {" "}
                            ({otpVerifyError.attempts_left}{" "}
                            {t("attempts_left") || "attempts left"})
                          </span>
                        )}
                      </p>
                    )}

                    <button
                      className="claim-submit-btn"
                      onClick={handleVerifyOTP}
                      disabled={otpVerifying || otpCode.length !== 6}
                    >
                      {otpVerifying ? t("loading") : t("otp_verify")}
                    </button>

                    <button
                      className="claim-resend-btn"
                      onClick={handleResendOTP}
                      disabled={countdown > 0}
                      style={{
                        marginTop: "10px",
                        background: countdown > 0 ? "#ccc" : "#f5f5f5",
                        color: countdown > 0 ? "#999" : "#333",
                        border: "1px solid #ddd",
                        padding: "10px 20px",
                        borderRadius: "5px",
                        cursor: countdown > 0 ? "not-allowed" : "pointer",
                        width: "100%",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      {countdown > 0
                        ? `${t("otp_resend_in")} ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, "0")}`
                        : t("otp_resend")}
                    </button>
                  </>
                )}

                <button
                  className="claim-logout-btn"
                  onClick={() => setShowLoginForm(false)}
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          </div>
        </>
      );
    }

    // Default locked view with login button
    return (
      <>
        <div className="binder">
          <p>Please use a mobile phone or tablet to use the page</p>
        </div>
        <div className="find-pet-info-container">
          <div className="find-pet-info-container-header">
            <div className="pet-photo-and-name-container">
              <div className="dropdown-menu-for-findpet-page">
                <button
                  className="top-login-btn"
                  onClick={() => setShowLoginForm(true)}
                >
                  {t("owner")}
                </button>

                <div className="find-pet-info-language-switcher">
                  {lngs.map((lng) => {
                    if (lng === localStorage.getItem("i18nextLng")) {
                      return null;
                    } else {
                      return (
                        <span
                          key={lng}
                          onClick={() => i18n.changeLanguage(lng)}
                          disabled={i18n.resolvedLanguage === lng}
                        >
                          {lngsNameForFindPetPage[lng]}
                        </span>
                      );
                    }
                  })}
                </div>
              </div>

              <img
                src={findPetInfo.pet_image || "/img/pet-placeholder.webp"}
                alt="Pet photo"
              />

              <div className="pet-name-and-mark-as-lost-button-container">
                <p className="pet-name">
                  {formatName(findPetInfo.pet_name) || t("unnamed_pet")}
                </p>
                <div className="vaccinated-container">
                  {findPetInfo.is_vaccinated ? (
                    <p className="vaccinated">#{t("vaccinated")}</p>
                  ) : (
                    <p className="not-vaccinated">#{t("not vaccinated")}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="find-pet-info-container-body">
            <div className="logo-button-container">
              <div className="logo-button">
                <Logo darkMode={darkMode} />
              </div>
              <div className="locked-info">
                <Lock color="#595959" strokeWidth={1.8} size={40} />
                <p className="locked-info-text">{t("information hidden")}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // For unlocked view (pet is lost) - handle login/edit modes
  if (isEditMode || ownerLoggedIn) {
    return (
      <>
        <div className="binder">
          <p>Please use a mobile phone or tablet to use the page</p>
        </div>
        <div className="find-pet-info-container claim-container">
          <div className="find-pet-info-container-header">
            <div className="dropdown-menu-for-findpet-page">
              {/* Language Switcher */}
              <div className="find-pet-info-language-switcher">
                {lngs.map((lng) => {
                  if (lng === localStorage.getItem("i18nextLng")) {
                    return null;
                  } else {
                    return (
                      <span
                        key={lng}
                        onClick={() => i18n.changeLanguage(lng)}
                        disabled={i18n.resolvedLanguage === lng}
                      >
                        {lngsNameForFindPetPage[lng]}
                      </span>
                    );
                  }
                })}
              </div>
            </div>

            {/* Pet Image Upload Section */}
            {/* <div className="form-section-title">
              {t("update_your_pet_details")}
            </div> */}
            <div className="form-group image-upload-group">
              <div className="image-upload-container">
                {editImagePreview ? (
                  <div className="image-preview-wrapper">
                    <img
                      src={editImagePreview}
                      alt="Pet preview"
                      className="pet-image-preview"
                    />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => {
                        setEditImagePreview(null);
                        setEditFormData((prev) => ({
                          ...prev,
                          pet_image: null,
                        }));
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="image-upload-label">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditImageChange}
                      className="image-upload-input"
                    />
                    <div className="image-upload-placeholder">
                      <ImagePlus {...uploadPetPhoto} />
                      <span>{t("add_pet_photo")}</span>
                    </div>
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="find-pet-info-container-body">
            <div className="logo-button-container">
              <div className="logo-button">
                <Logo darkMode={darkMode} />
              </div>
              {/* <h2 className="claim-title">{t("edit_pet_info")}</h2>
              <p className="claim-subtitle">{t("update_your_pet_details")}</p> */}
            </div>

            <div className="claim-form pet-info-form">
              {/* Pet Status Section */}
              <div className="form-section-title">{t("current_status")}</div>
              <div className="pet-status-section">
                <button
                  className={`status-toggle-btn switch ${
                    findPetInfo.marked_as_lost
                      ? "btn-mark-safe"
                      : "btn-mark-lost"
                  }`}
                  onClick={handleToggleLost}
                  role="switch"
                  aria-checked={findPetInfo.marked_as_lost}
                >
                  <span className="switch-label">{t("profile_open_lock")}</span>

                  <span className="switch-track">
                    <span className="switch-thumb" />
                  </span>
                </button>

                <div
                  className={`pet-status-card ${
                    findPetInfo.marked_as_lost ? "status-lost" : "status-safe"
                  }`}
                >
                  <div className="status-content">
                    <span className="status-description">
                      {findPetInfo.marked_as_lost
                        ? t("lost_status_description")
                        : t("safe_status_description")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Owner Information Section */}
              <div className="form-section-title">{t("owner_info")}</div>

              <div className="form-row">
                <div className="form-group">
                  <User {...iconStyle} />
                  <input
                    type="text"
                    name="full_name"
                    value={editFormData.full_name || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_name")}
                  />
                </div>

                <div className="form-group">
                  <Phone {...iconStyle} />
                  <input
                    type="tel"
                    name="phone_number"
                    value={editFormData.phone_number || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_phone")}
                  />
                </div>

                <div className="form-group">
                  <Phone {...iconStyle} />
                  <input
                    type="tel"
                    name="second_phone_number"
                    value={editFormData.second_phone_number || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_second_phone")}
                  />
                </div>

                <div className="form-group">
                  <Building2 {...iconStyle} />
                  <input
                    type="text"
                    name="city"
                    value={editFormData.city || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_city")}
                  />
                </div>

                <div className="form-group">
                  <MapPin {...iconStyle} />
                  <input
                    type="text"
                    name="address"
                    value={editFormData.address || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_address")}
                  />
                </div>
              </div>

              {/* Pet Information Section */}
              <div className="form-section-title">{t("pet_info")}</div>

              <div className="form-row">
                <div className="form-group">
                  <Dog {...iconStyle} />
                  <input
                    type="text"
                    name="pet_name"
                    value={editFormData.pet_name || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_pet_name")}
                  />
                </div>

                <div className="form-group">
                  <Dog {...iconStyle} />
                  <select
                    name="pet_type"
                    value={editFormData.pet_type || "dog"}
                    onChange={handleEditFormChange}
                  >
                    <option value="dog">{t("dog")}</option>
                    <option value="cat">{t("cat")}</option>
                    <option value="bird">{t("bird")}</option>
                  </select>
                </div>

                <div className="form-group">
                  <PawPrint {...iconStyle} />
                  <input
                    type="text"
                    name="pet_breed"
                    value={editFormData.pet_breed || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_breed")}
                  />
                </div>

                <div className="form-group">
                  <VenusAndMars {...iconStyle} />
                  <select
                    name="gender"
                    value={editFormData.gender || "male"}
                    onChange={handleEditFormChange}
                  >
                    <option value="male">{t("male")}</option>
                    <option value="female">{t("female")}</option>
                  </select>
                </div>

                <div className="form-group">
                  <Palette {...iconStyle} />
                  <input
                    type="text"
                    name="pet_color"
                    value={editFormData.pet_color || ""}
                    onChange={handleEditFormChange}
                    placeholder={t("enter_color")}
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <Syringe {...iconStyle} />
                <label>
                  <input
                    type="checkbox"
                    name="is_vaccinated"
                    checked={editFormData.is_vaccinated || false}
                    onChange={handleEditFormChange}
                  />
                  <span>{t("vaccinated")}</span>
                </label>
              </div>

              <div className="form-group">
                <MessageSquareMore {...iconStyle} />
                <textarea
                  name="comment"
                  value={editFormData.comment || ""}
                  onChange={handleEditFormChange}
                  placeholder={t("enter_comment")}
                  rows="3"
                />
              </div>

              <button className="claim-submit-btn" onClick={handleSaveEdit}>
                {t("save")}
              </button>

              <button className="claim-logout-btn" onClick={handleOwnerLogout}>
                {t("logout")}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show login form for unlocked view
  if (showLoginForm) {
    return (
      <>
        <div className="binder">
          <p>Please use a mobile phone or tablet to use the page</p>
        </div>
        <div className="find-pet-info-container">
          <div className="find-pet-info-container-header">
            <div className="pet-photo-and-name-container">
              <div className="dropdown-menu-for-findpet-page right">
                {/* Language Switcher */}
                <div className="find-pet-info-language-switcher login-page-lang-switcher">
                  {lngs.map((lng) => {
                    if (lng === localStorage.getItem("i18nextLng")) {
                      return null;
                    } else {
                      return (
                        <span
                          key={lng}
                          onClick={() => i18n.changeLanguage(lng)}
                          disabled={i18n.resolvedLanguage === lng}
                        >
                          {lngsNameForFindPetPage[lng]}
                        </span>
                      );
                    }
                  })}
                </div>
              </div>

              {/* Pet's picture above Logo */}
              <img
                src={findPetInfo.pet_image || "/img/pet-placeholder.webp"}
                alt="Pet photo"
              />

              <div className="pet-name-and-mark-as-lost-button-container">
                <p className="pet-name">
                  {formatName(findPetInfo.pet_name) || t("unnamed_pet")}
                </p>
                <div className="vaccinated-container">
                  {findPetInfo.is_vaccinated ? (
                    <p className="vaccinated">#{t("vaccinated")}</p>
                  ) : (
                    <p className="not-vaccinated">#{t("not vaccinated")}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="find-pet-info-container-body">
            <div className="logo-button-container">
              <div className="logo-button">
                <Logo darkMode={darkMode} />
              </div>
              <h3 className="claim-title">{t("owner_login")}</h3>
              {/* <p className="claim-subtitle" style={{ color: "#000" }}>
                {t("enter_phone_to_edit")}
              </p> */}
            </div>
            <div className="claim-form">
              {!otpSent ? (
                <>
                  {/* Step 1: Phone number input */}
                  <div className="form-group">
                    <label>{t("phone_number")}</label>
                    <input
                      type="tel"
                      value={loginPhoneNumber}
                      onChange={(e) => setLoginPhoneNumber(e.target.value)}
                      placeholder={t("enter_phone_placeholder")}
                      maxLength="15"
                    />
                  </div>

                  {otpSendError && (
                    <p
                      className="claim-error"
                      style={{
                        padding: "12px 16px",
                        fontSize: "0.9rem",
                        color: "#dc3545",
                        background: "#fff5f5",
                        border: "1px solid #ffebee",
                        borderRadius: "8px",
                        textAlign: "center",
                        marginBottom: "10px",
                      }}
                    >
                      {getTranslatedError(otpSendError.error, t) ||
                        t("otp_send_error")}
                      {countdown > 0 && (
                        <span
                          style={{
                            display: "block",
                            marginTop: "5px",
                            fontWeight: "600",
                            color: "#dc3545",
                          }}
                        >
                          {Math.floor(countdown / 60)}:
                          {(countdown % 60).toString().padStart(2, "0")}
                        </span>
                      )}
                    </p>
                  )}

                  <button
                    className="claim-submit-btn"
                    onClick={handleSendOTP}
                    disabled={otpSending}
                  >
                    {otpSending ? t("loading") : t("send_code")}
                  </button>

                  <button
                    className="claim-logout-btn"
                    onClick={() => setShowLoginForm(false)}
                  >
                    {t("cancel")}
                  </button>
                </>
              ) : (
                <>
                  {/* Step 2: OTP verification */}
                  <div className="form-group">
                    <label>{t("otp_enter_code")}</label>
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) =>
                        setOtpCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                      placeholder={t("otp_code_placeholder")}
                      maxLength="6"
                      style={{
                        textAlign: "center",
                        fontSize: "1rem",
                        letterSpacing: "0.5rem",
                      }}
                    />
                  </div>

                  <p
                    className="otp-sent-message"
                    style={{
                      color: "#666",
                      textAlign: "center",
                      marginBottom: "10px",
                    }}
                  >
                    {t("otp_sent")} ({loginPhoneNumber})
                  </p>

                  {otpVerifyError && (
                    <p className="claim-error">
                      {getTranslatedError(otpVerifyError.error, t) ||
                        t("otp_invalid_code")}
                      {otpVerifyError.attempts_left !== undefined && (
                        <span>
                          {" "}
                          ({otpVerifyError.attempts_left}{" "}
                          {t("attempts_left") || "attempts left"})
                        </span>
                      )}
                    </p>
                  )}

                  <button
                    className="claim-submit-btn"
                    onClick={handleVerifyOTP}
                    disabled={otpVerifying || otpCode.length !== 6}
                  >
                    {otpVerifying ? t("loading") : t("otp_verify")}
                  </button>

                  <button
                    className="claim-resend-btn"
                    onClick={handleResendOTP}
                    disabled={countdown > 0}
                    style={{
                      marginTop: "10px",
                      background: countdown > 0 ? "#ccc" : "#f5f5f5",
                      color: countdown > 0 ? "#999" : "#333",
                      border: "1px solid #ddd",
                      padding: "10px 20px",
                      borderRadius: "5px",
                      cursor: countdown > 0 ? "not-allowed" : "pointer",
                      width: "100%",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    {countdown > 0
                      ? `${t("otp_resend_in")} ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, "0")}`
                      : t("otp_resend")}
                  </button>

                  <button
                    className="claim-logout-btn"
                    onClick={() => {
                      clearOTPState();
                      setShowLoginForm(false);
                    }}
                    style={{ marginTop: "10px" }}
                  >
                    {t("cancel")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="binder">
        <p>Please use a mobile phone or tablet to use the page</p>
      </div>

      <div className="popup-gps-alert-parent-container">
        <div className="popup-gps-alert">
          <h2>{t("this pet may be lost")}</h2>
          <div className="gps-alert-main-text-container">
            <span>{t("gps-popup-alert-first-text")}</span>
            <span className="pet-name-in-alert-text">
              {findPetInfo.pet_name}
            </span>
            <span>{t("gps-popup-alert-second-text")}</span>

            <br />
            <p>{t("gps-popup-alert-permission-text")}</p>
          </div>
          <button className="approve">{t("send")}</button>
          <button className="cancel">{t("ignore")}</button>
        </div>
      </div>

      <div className="find-pet-info-container">
        <div className="find-pet-info-container-header">
          <div className="pet-photo-and-name-container">
            <div className="dropdown-menu-for-findpet-page">
              <button
                className="top-login-btn"
                onClick={() => setShowLoginForm(true)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#000",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  padding: "8px 12px",
                }}
              >
                {t("owner")}
              </button>

              <div className="find-pet-info-language-switcher">
                {lngs.map((lng) => {
                  if (lng === localStorage.getItem("i18nextLng")) {
                    return null;
                  } else {
                    return (
                      <span
                        key={lng}
                        onClick={() => i18n.changeLanguage(lng)}
                        disabled={i18n.resolvedLanguage === lng}
                      >
                        {lngsNameForFindPetPage[lng]}
                      </span>
                    );
                  }
                })}
              </div>
            </div>
            <img
              src={findPetInfo.pet_image || "/img/pet-placeholder.webp"}
              alt="Pet photo"
            />
            <span className="lost-pet-text">{t("im lost")} 🥺</span>

            <div className="pet-name-and-mark-as-lost-button-container">
              <p className="pet-name">
                {formatName(findPetInfo.pet_name) || t("unnamed_pet")}
              </p>
              <div className="vaccinated-container">
                {findPetInfo.is_vaccinated ? (
                  <p className="vaccinated">#{t("vaccinated")}</p>
                ) : (
                  <p className="not-vaccinated">#{t("not vaccinated")}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="find-pet-info-container-body">
          <div className="logo-button-container">
            <div className="logo-button">
              <Logo darkMode={darkMode} />
            </div>
          </div>

          <div className="find-pet-info-call-owner">
            <a className="call-owner" href={`tel:${findPetInfo.phone_number}`}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="1.5 -1.5 24 24">
                <g>
                  <path d="M17.62 10.75a.77.77 0 01-.77-.77c0-.37-.37-1.14-.99-1.81-.61-.65-1.28-1.03-1.84-1.03a.77.77 0 01-.77-.77c0-.42.35-.77.77-.77 1 0 2.05.54 2.97 1.51.86.91 1.41 2.04 1.41 2.86 0 .43-.35.78-.78.78zM21.23 10.75a.77.77 0 01-.77-.77c0-3.55-2.89-6.43-6.43-6.43a.77.77 0 01-.77-.77c0-.42.34-.78.76-.78C18.42 2 22 5.58 22 9.98c0 .42-.35.77-.77.77z"></path>
                  <path d="M11.79 14.21l-3.27 3.27c-.36-.32-.71-.65-1.05-.99a28.414 28.414 0 01-2.79-3.27c-.82-1.14-1.48-2.28-1.96-3.41C2.24 8.67 2 7.58 2 6.54c0-.68.12-1.33.36-1.93.24-.61.62-1.17 1.15-1.67C4.15 2.31 4.85 2 5.59 2c.28 0 .56.06.81.18.26.12.49.3.67.56l2.32 3.27c.18.25.31.48.4.7.09.21.14.42.14.61 0 .24-.07.48-.21.71-.13.23-.32.47-.56.71l-.76.79c-.11.11-.16.24-.16.4 0 .08.01.15.03.23.03.08.06.14.08.2.18.33.49.76.93 1.28.45.52.93 1.05 1.45 1.58.36.35.71.69 1.06.99z"></path>
                  <path d="M21.97 18.33a2.54 2.54 0 01-.25 1.09c-.17.36-.39.7-.68 1.02-.49.54-1.03.93-1.64 1.18-.01 0-.02.01-.03.01-.59.24-1.23.37-1.92.37-1.02 0-2.11-.24-3.26-.73s-2.3-1.15-3.44-1.98c-.39-.29-.78-.58-1.15-.89l3.27-3.27c.28.21.53.37.74.48.05.02.11.05.18.08.08.03.16.04.25.04.17 0 .3-.06.41-.17l.76-.75c.25-.25.49-.44.72-.56.23-.14.46-.21.71-.21.19 0 .39.04.61.13.22.09.45.22.7.39l3.31 2.35c.26.18.44.39.55.64.1.25.16.5.16.78z"></path>
                </g>
              </svg>
              {/* {t("call the owner")} */}
            </a>

            <a
              className="message-owner-on-phone"
              href={`sms:${findPetInfo.phone_number}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g>
                  <path d="M2 12.97V6.99C2 4.23 4.24 2 7 2h10c2.76 0 5 2.23 5 4.99v6.98c0 2.75-2.24 4.98-5 4.98h-1.5c-.31 0-.61.15-.8.4l-1.5 1.99c-.66.88-1.74.88-2.4 0l-1.5-1.99c-.16-.22-.52-.4-.8-.4H7c-2.76 0-5-2.23-5-4.98v-1z"></path>
                  <path d="M17 8.75H7c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h10c.41 0 .75.34.75.75s-.34.75-.75.75zM13 13.75H7c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h6c.41 0 .75.34.75.75s-.34.75-.75.75z"></path>
                </g>
              </svg>
              {/* {t("call the owner")} */}
            </a>

            {/* <button
              className="message-owner"
              onClick={() => {
                clearChatUsers();
                addNewChatUser(findPetInfo.user);
                router.navigate("/messages", { replace: true });
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g>
                  <path d="M2 12.97V6.99C2 4.23 4.24 2 7 2h10c2.76 0 5 2.23 5 4.99v6.98c0 2.75-2.24 4.98-5 4.98h-1.5c-.31 0-.61.15-.8.4l-1.5 1.99c-.66.88-1.74.88-2.4 0l-1.5-1.99c-.16-.22-.52-.4-.8-.4H7c-2.76 0-5-2.23-5-4.98v-1z"></path>
                  <path d="M17 8.75H7c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h10c.41 0 .75.34.75.75s-.34.75-.75.75zM13 13.75H7c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h6c.41 0 .75.34.75.75s-.34.75-.75.75z"></path>
                </g>
              </svg>
              {t("message")}
            </button> */}

            <button className="send-location" onClick={handleShareLocation}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="4.7 2 17 17"
              >
                <path d="M16.205 5.265l-6.49 2.164h0c-1.634.544-2.45.816-2.776 1.129a2 2 0 000 2.884c.325.313 1.142.585 2.775 1.13h0c.33.11.494.164.64.241a2 2 0 01.833.833c.077.146.132.31.242.64.544 1.633.816 2.45 1.129 2.775a2 2 0 002.884 0c.313-.325.585-1.142 1.13-2.775h0l2.163-6.491c.552-1.656.828-2.484.391-2.921-.437-.437-1.265-.161-2.92.39z"></path>
              </svg>
              {/* {t("share location")} */}
            </button>

            {/* <button className="look-on-map">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g>
                  <path d="M19.88 20.94c-.95.7-2.2 1.06-3.69 1.06H7.81c-.24 0-.48-.01-.71-.04l6.9-6.9 5.88 5.88zM22 7.81v8.38c0 1.49-.36 2.74-1.06 3.69L15.06 14l6.9-6.9c.03.23.04.47.04.71z"></path>
                  <path d="M15.06 14l5.88 5.88c-.29.42-.64.77-1.06 1.06L14 15.06l-6.9 6.9c-.64-.04-1.22-.17-1.75-.37C3.21 20.81 2 18.91 2 16.19V7.81C2 4.17 4.17 2 7.81 2h8.38c2.72 0 4.62 1.21 5.4 3.35.2.53.33 1.11.37 1.75l-6.9 6.9z"></path>
                  <path d="M15.06 14l5.88 5.88c-.29.42-.64.77-1.06 1.06L14 15.06l-6.9 6.9c-.64-.04-1.22-.17-1.75-.37l.39-.39L21.59 5.35c.2.53.33 1.11.37 1.75l-6.9 6.9zM12.24 7.93c-.38-1.65-1.84-2.39-3.12-2.4-1.28 0-2.74.74-3.12 2.39-.42 1.83.7 3.36 1.71 4.32.4.38.9.56 1.41.56.51 0 1.01-.19 1.41-.56 1.01-.96 2.13-2.49 1.71-4.31zM9.15 9.49c-.55 0-1-.45-1-1s.44-1 1-1h.01c.55 0 1 .45 1 1s-.46 1-1.01 1z"></path>
                </g>
              </svg>
              {t("location")}
            </button> */}
          </div>

          {/* <div className="vaccinated-container">
            {findPetInfo.is_vaccinated ? (
              <>
                <p className="vaccinated">{t("vaccinated")}</p>
              </>
            ) : null}
          </div> */}

          <div className="find-pet-info-outside-container">
            <div className="find-pet-info">
              <div className="find-pet-info-header">
                <span className="header-text">{t("owner")}</span>
              </div>
              <div className="label-container">
                <User {...iconStyle} />
                <p>{t(findPetInfo.full_name)}</p>
              </div>
              <div className="label-container for-user-mobile-number">
                <div className="user-mobile-number-left-container">
                  <Smartphone {...iconStyle} />
                  <p>{t(findPetInfo.phone_number)}</p>
                </div>

                <div className="user-mobile-number-right-container">
                  <a
                    className="call-owner"
                    href={`tel:${findPetInfo.phone_number}`}
                  >
                    <Phone {...iconStyle2} />
                  </a>
                  <a
                    className="message-owner-on-phone"
                    href={`sms:${findPetInfo.phone_number}`}
                  >
                    <Mail {...iconStyle2} />
                  </a>
                </div>
              </div>

              {findPetInfo.second_phone_number ? (
                <>
                  <div className="label-container for-user-mobile-number">
                    <div className="user-mobile-number-left-container">
                      <Smartphone {...iconStyle} />
                      <p>{t(findPetInfo.second_phone_number)}</p>
                    </div>

                    <div className="user-mobile-number-right-container">
                      <a
                        className="call-owner"
                        href={`tel:${findPetInfo.second_phone_number}`}
                      >
                        <Phone {...iconStyle2} />
                      </a>
                      <a
                        className="message-owner-on-phone"
                        href={`sms:${findPetInfo.second_phone_number}`}
                      >
                        <Mail {...iconStyle2} />
                      </a>
                    </div>
                  </div>
                </>
              ) : null}

              <div className="label-container">
                <Building2 {...iconStyle} />
                <p>{t(findPetInfo.city)}</p>
              </div>

              <div className="label-container">
                <MapPin {...iconStyle} />
                <p>{t(findPetInfo.address)}</p>
              </div>
            </div>

            <div className="find-pet-info none">
              <div className="find-pet-info-header">
                <span className="header-text">{t("pet info")}</span>
              </div>
              <div className="label-container pet-type">
                <span>{t("animal")}</span>
                <p>{t(findPetInfo.pet_type)}</p>
              </div>
              <div className="label-container">
                <span>{t("breed")}</span>
                <p>{t(findPetInfo.pet_breed)}</p>
              </div>
              <div className="label-container">
                <span>{t("gender")}</span>
                <p>{t(findPetInfo.gender)}</p>
              </div>
              <div className="label-container">
                <span>{t("pet color")}</span>
                <p>{t(findPetInfo.pet_color)}</p>
              </div>

              <div className="label-container">
                <span>{t("vaccinated")}</span>
                {findPetInfo.is_vaccinated ? (
                  <>
                    <p className="vaccinated">{t("vaccinated")}</p>
                  </>
                ) : (
                  <>
                    <p className="not-vaccinated">{t("not vaccinated")}</p>
                  </>
                )}
              </div>
            </div>

            <button
              className="share-location-to-owner"
              onClick={handleShareLocation}
            >
              {/* <MapPinned {...iconStyle} /> */}
              {t("share location to owner")}
              <MapPinned {...iconStyle2} />
            </button>
          </div>

          <div className="pet-info-in-little-box">
            <span className="pet-info-in-little-box-header">
              {t("pet info")}
            </span>
            <div>
              <Dog {...iconStyle} />
              <p>{t(findPetInfo.pet_type)}</p>
            </div>
            <div>
              <PawPrint {...iconStyle} />
              <p>{t(findPetInfo.pet_breed)}</p>
            </div>
            <div>
              <VenusAndMars {...iconStyle} />
              <p>{t(findPetInfo.gender)}</p>
            </div>
            <div>
              <Palette {...iconStyle} />
              <p>{t(findPetInfo.pet_color)}</p>
            </div>

            <div className="additional-comment">
              <span className="pet-info-in-little-box-header">
                {t("additional info")}
              </span>
              <p>{t(findPetInfo.comment)}</p>
            </div>
          </div>
        </div>
      </div>
      <Outlet />

      {showLocationDenied &&
        (() => {
          const ua = navigator.userAgent || "";
          const platform = /iPhone|iPad|iPod/.test(ua)
            ? "ios"
            : /Android/.test(ua)
              ? "android"
              : "desktop";
          return (
            <div
              className="location-denied-overlay"
              onClick={() => setShowLocationDenied(false)}
            >
              <div
                className="location-denied-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="location-denied-icon">
                  <MapPinned size={50} color="#ff6500" />
                </div>
                <h3>{t("location_access_blocked")}</h3>
                <p>{t("location_denied_instructions")}</p>
                <div className="location-denied-steps">
                  <p>1. {t(`location_${platform}_step_1`)}</p>
                  <p>2. {t(`location_${platform}_step_2`)}</p>
                  <p>3. {t(`location_${platform}_step_3`)}</p>
                </div>
                <button
                  className="location-denied-retry"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  {t("reload_page")}
                </button>
                <button
                  className="location-denied-close"
                  onClick={() => setShowLocationDenied(false)}
                >
                  {t("dismiss")}
                </button>
              </div>
            </div>
          );
        })()}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    findPetInfo: state.orders.findPetInfo,
    claimLoading: state.orders.claimLoading,
    claimSuccess: state.orders.claimSuccess,
    claimError: state.orders.claimError,
    ownerLoginLoading: state.orders.ownerLoginLoading,
    ownerLoggedIn: state.orders.ownerLoggedIn,
    ownerLoginError: state.orders.ownerLoginError,
    // OTP state
    otpSending: state.orders.otpSending,
    otpSent: state.orders.otpSent,
    otpSendError: state.orders.otpSendError,
    otpPhoneNumber: state.orders.otpPhoneNumber,
    otpVerifying: state.orders.otpVerifying,
    otpVerifyError: state.orders.otpVerifyError,
    otpSecondsRemaining: state.orders.otpSecondsRemaining,
  };
};

export default connect(mapStateToProps, {
  getFindPetInfo,
  sendLocation,
  claimQRCode,
  updateClaimedQR,
  clearClaimState,
  ownerLogin,
  ownerUpdateQR,
  ownerToggleLost,
  ownerLogout,
  sendOTP,
  verifyOTP,
  clearOTPState,
  addNewChatUser,
  clearChatUsers,
})(FindPet);
